// filepath: apps/web/src/features/admin/templates/registry/useTemplateRegistry.ts
//
// FR-UI-ADM-11 AC-1 -- TanStack Query hook for the template
// registry browse list (GET /api/templates/registry).
//
// INV-1 (cross-tenant): the cache key includes `tenantSlug` as
// the 2nd segment. Switching tenants invalidates the prior
// tenant's cache and refetches the new tenant's view — the
// server-side `publisher_kind='platform'` filter is the
// authoritative line of defense.
//
// INV-5 / INV-9 (central): the response shape carries only
// `template_id, version, description, content_hash,
// publisher_kind, pre_vetted, published_at, published_by` — NO
// `manifest_json`, `payload_zip`, or `tenant_id`. The server-side
// `TestTemplateRegistryHandler_List_NeverReturnsBundlePayload`
// walker enforces this at the wire-shape level; the SPA-side
// static guard enforces it at the type-definition level.

import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  TemplateRegistryFetchError,
  TemplateRegistryListResponse,
} from "./types";

/**
 * Cache keys for the template registry admin editor. tenantSlug
 * is the 2nd segment (INV-1). Mirrors the FR-UI-ADM-1 adminKeys
 * shape and the FR-UI-ADM-10 secretKeys shape; co-located here so
 * the templates surface is self-contained.
 */
export const templateRegistryKeys = {
  registry: (tenantSlug: string) =>
    ["admin", tenantSlug, "template-registry"] as const,
};

/**
 * useTemplateRegistry -- GET /api/templates/registry.
 *
 * Returns the typed union `TemplateRegistryFetchError` on
 * failure; the page maps each kind to its dedicated error
 * component. The query is enabled for both staging and
 * production tenants (the browse endpoint is read-only and
 * accessible from both environments); only the install verb
 * is staging-only.
 */
export function useTemplateRegistry() {
  const { tenantSlug } = useTenant();
  return useQuery<TemplateRegistryListResponse, ApiError>({
    queryKey: templateRegistryKeys.registry(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/templates/registry");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as TemplateRegistryListResponse;
    },
  });
}

/**
 * classifyTemplateRegistryFetchError -- maps a transport-level
 * ApiError to the typed `TemplateRegistryFetchError` union.
 * Mirrors the FR-UI-ADM-10 classifySecretFetchError shape; used
 * by TemplateRegistryPage to render one of the five
 * FR-UI-CORE-10 fetch states.
 */
export function classifyTemplateRegistryFetchError(
  err: ApiError,
): TemplateRegistryFetchError {
  switch (err.status) {
    case 403:
      return {
        kind: "permission_denied",
        message: err.message || "permission denied",
        retriable: false,
      };
    case 429:
      return {
        kind: "rate_limit",
        message: err.message || "rate limited",
        retriable: true,
      };
    case 0:
      return {
        kind: "fetch_failure",
        message: err.message || "network error",
        retriable: true,
      };
    default:
      return {
        kind: "fetch_failure",
        message: err.message || err.code || "fetch failure",
        retriable: err.status >= 500,
      };
  }
}
